/**
 * Cart Drawer — Web Component
 * Handles open/close, AJAX cart operations, quantity updates,
 * item removal, and section re-rendering.
 */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.sectionUrl = window.routes && window.routes.cart_url
        ? window.routes.cart_url + '?section_id=cart-drawer'
        : '/cart?section_id=cart-drawer';

      this.bindCloseButtons();
      this.bindCartToggles();
      this.bindAddToCartForms();
      this.bindEscape();
      this.initQuantityElements();
      this.initRemoveButtons();
      this.initFreeShippingBar();
    }

    bindCloseButtons() {
      this.addEventListener('click', (e) => {
        const closeTarget = e.target.closest('[data-drawer-close]');
        if (closeTarget) {
          const isNavLink = closeTarget.tagName === 'A' && closeTarget.getAttribute('href') && closeTarget.getAttribute('href') !== '#';
          if (!isNavLink) e.preventDefault();
          this.close();
        }
      });
    }

    bindEscape() {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    bindCartToggles() {
      // Use document-level delegation so it works regardless of timing
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-cart-toggle]');
        if (!toggle) return;
        e.preventDefault();
        e.stopPropagation();
        this.open();
      });
    }

    bindAddToCartForms() {
      document.addEventListener('submit', (e) => {
        const form = e.target;
        // Only intercept product add-to-cart forms
        const addBtn = form.querySelector('[data-add-to-cart]');
        if (!addBtn) return;
        if (form.closest('cart-drawer')) return;

        e.preventDefault();
        this.addToCart(form, addBtn);
      });
    }

    isOpen() {
      return this.classList.contains('is-visible');
    }

    open() {
      this.classList.add('is-visible');
      document.body.classList.add('overflow-hidden');
      this.setAttribute('aria-hidden', 'false');
      if (typeof window.FocusTrap !== 'undefined') {
        this._focusTrap = window.FocusTrap.activate(this, {
          onEscape: () => this.close()
        });
      }
    }

    close() {
      this.classList.remove('is-visible');
      document.body.classList.remove('overflow-hidden');
      this.setAttribute('aria-hidden', 'true');
      if (this._focusTrap) {
        this._focusTrap.deactivate();
        this._focusTrap = null;
      }
    }

    async addToCart(form, button) {
      if (this._addToCartInFlight) return;
      this._addToCartInFlight = true;

      const textEl = button.querySelector('[data-add-to-cart-text]');
      const originalHTML = textEl ? textEl.innerHTML : '';

      button.setAttribute('disabled', 'disabled');
      button.classList.add('loading');

      const formData = new FormData(form);

      try {
        const response = await fetch(window.routes.cart_add_url + '.js', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || errorData.message || 'Could not add to cart');
        }

        await this.refreshDrawer();
        this.open();
        this.updateCartCount();
      } catch (error) {
        console.error('Add to cart error:', error);
        alert(error.message);
      } finally {
        this._addToCartInFlight = false;
        button.removeAttribute('disabled');
        button.classList.remove('loading');
        if (textEl) textEl.innerHTML = originalHTML;
      }
    }

    async refreshDrawer() {
      try {
        const response = await fetch(this.sectionUrl);
        if (!response.ok) throw new Error('Failed to fetch cart drawer');

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newBody = doc.querySelector('#cart-drawer-body-content');
        const currentBody = this.querySelector('#cart-drawer-body-content');
        if (newBody && currentBody) {
          currentBody.innerHTML = newBody.innerHTML;
          currentBody.className = newBody.className;
          currentBody.setAttribute('data-drawer-cart-count', newBody.getAttribute('data-drawer-cart-count') || '0');
          currentBody.setAttribute('data-drawer-cart-price', newBody.getAttribute('data-drawer-cart-price') || '0');
        }

        const newFoot = doc.querySelector('#cart-drawer-summary');
        const currentFoot = this.querySelector('#cart-drawer-summary');
        if (newFoot) {
          if (currentFoot) {
            currentFoot.outerHTML = newFoot.outerHTML;
          } else {
            const drawerBase = this.querySelector('.drawer-base');
            if (drawerBase) drawerBase.insertAdjacentHTML('beforeend', newFoot.outerHTML);
          }
        } else if (currentFoot) {
          currentFoot.remove();
        }

        const newHeading = doc.querySelector('.drawer-heading');
        const currentHeading = this.querySelector('.drawer-heading');
        if (newHeading && currentHeading) {
          currentHeading.innerHTML = newHeading.innerHTML;
        }

        this.initQuantityElements();
        this.initRemoveButtons();
        this.initFreeShippingBar();
        this.initSwipers();

      } catch (error) {
        console.error('Refresh drawer error:', error);
      }
    }

    initQuantityElements() {
      this.querySelectorAll('quantity-element').forEach(el => {
        if (el.dataset.initialized) return;
        el.dataset.initialized = 'true';

        const minus = el.querySelector('.quantity-minus');
        const plus = el.querySelector('.quantity-plus');
        const input = el.querySelector('.quantity-input');

        if (!input) return;

        const updateQty = (newVal) => {
          const min = parseInt(input.min) || 1;
          const max = input.max ? parseInt(input.max) : Infinity;
          newVal = Math.max(min, Math.min(max, newVal));
          input.value = newVal;
          this.updateQuantity(input);
        };

        if (minus) {
          minus.addEventListener('click', () => {
            updateQty(parseInt(input.value) - (parseInt(input.step) || 1));
          });
        }

        if (plus) {
          plus.addEventListener('click', () => {
            updateQty(parseInt(input.value) + (parseInt(input.step) || 1));
          });
        }

        input.addEventListener('change', () => {
          updateQty(parseInt(input.value) || 1);
        });
      });
    }

    async updateQuantity(input) {
      const index = input.dataset.index;
      const quantity = parseInt(input.value);

      try {
        const response = await fetch(window.routes.cart_change_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: index, quantity: quantity }),
        });

        if (!response.ok) throw new Error('Failed to update quantity');

        await this.refreshDrawer();
        this.updateCartCount();
      } catch (error) {
        console.error('Update quantity error:', error);
      }
    }

    initRemoveButtons() {
      this.querySelectorAll('[data-remove-item]').forEach(btn => {
        if (btn.dataset.initialized) return;
        btn.dataset.initialized = 'true';

        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          const line = btn.dataset.line;
          const loader = btn.closest('cart-remove-item')?.querySelector('.cart-remove-loader');
          if (loader) {
            loader.classList.remove('hidden');
            loader.classList.add('loading');
          }

          try {
            const response = await fetch(window.routes.cart_change_url + '.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ line: parseInt(line), quantity: 0 }),
            });

            if (!response.ok) throw new Error('Failed to remove item');

            await this.refreshDrawer();
            this.updateCartCount();
          } catch (error) {
            console.error('Remove item error:', error);
            if (loader) {
              loader.classList.add('hidden');
              loader.classList.remove('loading');
            }
          }
        });
      });
    }

    initFreeShippingBar() {
      const bar = this.querySelector('[data-main-shipping-bar]');
      if (!bar) return;

      const rate = parseFloat(typeof currencyRate !== 'undefined' ? currencyRate : 0);
      if (!rate || isNaN(rate)) {
        bar.classList.add('hidden');
        return;
      }

      const bodyEl = this.querySelector('#cart-drawer-body-content');
      const cartPrice = parseFloat(bodyEl?.dataset.drawerCartPrice || 0) / 100;
      const remaining = Math.max(0, rate - cartPrice);
      const progress = Math.min(100, (cartPrice / rate) * 100);

      const messageEl = bar.querySelector('.free-shipping-bar-message');
      const progressEl = bar.querySelector('.progress');

      if (remaining <= 0) {
        if (messageEl) messageEl.textContent = typeof shippingsuccessmessage !== 'undefined' && shippingsuccessmessage ? shippingsuccessmessage : 'You qualify for free shipping!';
      } else {
        const money = new Intl.NumberFormat(window.Shopify?.locale || 'en-AU', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'AUD',
        }).format(remaining);
        const msg = typeof shippingmessage !== 'undefined' && shippingmessage
          ? shippingmessage.replace('||price||', money).replace('[amount]', money).replace('{{ amount }}', money)
          : "You're " + money + ' away from free shipping';
        if (messageEl) messageEl.textContent = msg;
      }

      if (progressEl) progressEl.style.width = progress + '%';

      bar.classList.remove('hidden');
    }

    initSwipers() {
      var sliders = this.querySelectorAll('slider-component');
      if (!sliders.length) return;

      var initAll = () => {
        sliders.forEach(el => {
          const settingsAttr = el.dataset.settings;
          if (!settingsAttr) return;

          try {
            const settings = JSON.parse(settingsAttr);
            const swiperEl = el.querySelector('.swiper') || el;
            if (swiperEl.swiper) swiperEl.swiper.destroy();

            // Resolve navigation elements from parent container
            const parent = el.closest('product-recommendations') || el.parentElement;
            if (settings.navigation && parent) {
              if (typeof settings.navigation.nextEl === 'string') {
                settings.navigation.nextEl = parent.querySelector(settings.navigation.nextEl) || settings.navigation.nextEl;
              }
              if (typeof settings.navigation.prevEl === 'string') {
                settings.navigation.prevEl = parent.querySelector(settings.navigation.prevEl) || settings.navigation.prevEl;
              }
            }

            new Swiper(swiperEl, settings);
          } catch (e) {
            // Swiper init failed silently
          }
        });
      };

      if (typeof Swiper !== 'undefined') {
        initAll();
      } else if (window.loadSwiper) {
        window.loadSwiper().then(initAll);
      }
    }

    async updateCartCount() {
      try {
        const response = await fetch(window.routes.cart_url + '.js');
        const cart = await response.json();
        const count = cart.item_count;

        // Update header cart count badges only (not the drawer body)
        document.querySelectorAll('.header__cart-count, [data-header-cart-count]').forEach(el => {
          el.textContent = count;
          if (count > 0) {
            el.classList.remove('hidden');
          } else {
            el.classList.add('hidden');
          }
        });

        // Update drawer heading count
        const results = this.querySelector('.drawer-results');
        if (results) results.textContent = '(' + count + ')';
      } catch (e) {
        // Silently fail
      }
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

/* Define all custom elements used in the cart drawer */
[
  'quantity-element', 'cart-remove-item', 'close-btn', 'free-shipping-bar',
  'cart-items-data', 'cart-item', 'cart-item-info', 'product-price-list',
  'product-sale-price', 'shipping-tax', 'gift-wrapping-content',
  'product-recommendations', 'modal-dialog-head', 'modal-dialog-form',
  'modal-dialog-foot', 'cart-dialog', 'cart-note-content'
].forEach(tag => {
  if (!customElements.get(tag)) {
    customElements.define(tag, class extends HTMLElement {});
  }
});

/* Slider Component — if not already defined */
if (!customElements.get('slider-component')) {
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.initSwiper();
    }

    initSwiper() {
      var self = this;
      function doInit() {
        const settingsAttr = self.dataset.settings;
        if (!settingsAttr) return;

        try {
          const settings = JSON.parse(settingsAttr);

          // Resolve navigation elements from parent container
          const parent = self.closest('product-recommendations') || self.parentElement;
          if (settings.navigation && parent) {
            if (typeof settings.navigation.nextEl === 'string') {
              settings.navigation.nextEl = parent.querySelector(settings.navigation.nextEl) || settings.navigation.nextEl;
            }
            if (typeof settings.navigation.prevEl === 'string') {
              settings.navigation.prevEl = parent.querySelector(settings.navigation.prevEl) || settings.navigation.prevEl;
            }
          }

          new Swiper(self, settings);
        } catch (e) {
          // Swiper init failed
        }
      }

      if (typeof Swiper !== 'undefined') {
        doInit();
      } else if (window.loadSwiper) {
        window.loadSwiper().then(doInit);
      } else {
        setTimeout(() => this.initSwiper(), 200);
      }
    }
  }
  customElements.define('slider-component', SliderComponent);
}
